import type { ApolloClient } from '@apollo/client'
import { useLazyQuery } from '@apollo/client/react'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'

export const commentsScoreQuery = graphql(/* GraphQL */ `
  query CommentsScoreForChannelsQuery($input: CommentsScoreForChannelsInput!) {
    commentsScoreForChannels(input: $input) {
      summary {
        currentAchievementLevel {
          id
          label
        }
        currentPeriodScore {
          totalValue
          responseRate {
            value
            percentage
          }
          responseSpeed {
            value
            percentage
          }
          consistency {
            value
            percentage
          }
        }
        previousPeriodScore {
          totalValue
          responseRate {
            value
            percentage
          }
          responseSpeed {
            value
            percentage
          }
          consistency {
            value
            percentage
          }
        }
      }
      channelsScores {
        channelId
        scoreDetails {
          currentAchievementLevel {
            id
            label
          }
          currentPeriodScore {
            totalValue
            responseRate {
              value
              percentage
            }
            responseSpeed {
              value
              percentage
            }
            consistency {
              value
              percentage
            }
          }
          previousPeriodScore {
            totalValue
            responseRate {
              value
              percentage
            }
            responseSpeed {
              value
              percentage
            }
            consistency {
              value
              percentage
            }
          }
        }
      }
    }
  }
`)

type CommentsScoreData = ResultOf<typeof commentsScoreQuery>

type UseCommentsScoreQueryReturn = {
  fetchCommentsScore: (params: {
    organizationId: string
    channelIds: string[]
  }) => Promise<ApolloClient.QueryResult<CommentsScoreData> | null>
}

/**
 * Hook to fetch the comments score for a given organization and channels.
 * @returns {UseCommentsScoreQueryReturn} An object containing the fetchCommentsScore function
 */
export function useCommentsScoreQuery(): UseCommentsScoreQueryReturn {
  const [getCommentsScore] = useLazyQuery(commentsScoreQuery, {
    fetchPolicy: 'no-cache',
  })

  const fetchCommentsScore = async (params: {
    organizationId: string
    channelIds: string[]
  }): Promise<ApolloClient.QueryResult<CommentsScoreData> | null> => {
    const { organizationId, channelIds } = params

    // Defense-in-depth: the backend guard rejects an empty `channelIds`, so
    // never hit the API without at least one channel to score.
    if (channelIds.length === 0) {
      return null
    }

    return await getCommentsScore({
      variables: {
        input: {
          organizationId,
          channelIds,
        },
      },
    })
  }

  return { fetchCommentsScore }
}
